import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  ContentCopy as CopyIcon,
  DashboardCustomize as DashboardIcon,
  DeleteOutline as DeleteIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { DataSourcesPanel, DashboardTemplatePicker } from '@signals/dashboard-ui';
import type { DashboardTemplate } from '@signals/dashboard-ui';
import {
  createSavedDashboard,
  fetchDashboardList,
  fetchDashboard,
  apiCreateDashboard,
  apiDeleteDashboard,
  loadUserContentLibrary,
  saveUserContentLibrary,
  upsertDashboard,
} from '../features/userContent/contentLibraryStorage';
import type { SavedDashboard } from '../features/userContent/types';

function formatUpdated(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

const UserContentPage: React.FC = () => {
  const navigate = useNavigate();
  const [dashboards, setDashboards] = useState<SavedDashboard[]>([]);
  const [loading, setLoading] = useState(true);
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('New dashboard');
  const chosenTemplate = useRef<DashboardTemplate | null>(null);

  const loadDashboards = useCallback(async () => {
    try {
      const list = await fetchDashboardList();
      setDashboards(list);

      // One-time migration: push any localStorage dashboards to the server
      const local = loadUserContentLibrary();
      if (local.dashboards.length > 0) {
        const serverIds = new Set(list.map((d) => d.id));
        for (const ld of local.dashboards) {
          if (!serverIds.has(ld.id)) {
            try {
              await apiCreateDashboard(ld);
            } catch { /* already exists or server error — skip */ }
          }
        }
        // Clear localStorage after migration
        try { localStorage.removeItem('pathfinder_user_content_v1'); } catch { /* noop */ }
        // Reload from server to get the migrated dashboards
        const refreshed = await fetchDashboardList();
        setDashboards(refreshed);
      }
    } catch {
      // API unavailable — fall back to localStorage
      const local = loadUserContentLibrary();
      setDashboards(local.dashboards);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadDashboards(); }, [loadDashboards]);

  const sortedDashboards = useMemo(
    () => [...dashboards].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)),
    [dashboards],
  );

  const handleTemplateSelected = (tpl: DashboardTemplate) => {
    chosenTemplate.current = tpl;
    setTemplatePickerOpen(false);
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    const tpl = chosenTemplate.current;
    const state = tpl ? { ...tpl.build(), templateId: tpl.id } : undefined;
    const dash = createSavedDashboard(newName.trim() || 'Untitled', state);
    setCreateOpen(false);
    setNewName('New dashboard');
    chosenTemplate.current = null;
    try {
      await apiCreateDashboard(dash);
    } catch {
      // Fallback: save to localStorage
      const lib = loadUserContentLibrary();
      saveUserContentLibrary(upsertDashboard(lib, dash));
    }
    navigate(`/dashboard-builder/${dash.id}?edit=true`);
  };

  const handleDuplicate = async (d: SavedDashboard) => {
    // Need full state for duplication — fetch it from server
    let fullDash = d;
    if (Object.keys(d.state.widgets).length === 0) {
      const fetched = await fetchDashboard(d.id);
      if (fetched) fullDash = fetched;
    }
    const copy = createSavedDashboard(`${fullDash.name} (copy)`, fullDash.state);
    try {
      await apiCreateDashboard(copy);
      await loadDashboards();
    } catch {
      const lib = loadUserContentLibrary();
      saveUserContentLibrary(upsertDashboard(lib, copy));
      setDashboards((prev) => [...prev, copy]);
    }
  };

  const handleDelete = async (id: string) => {
    setDashboards((prev) => prev.filter((d) => d.id !== id));
    try {
      await apiDeleteDashboard(id);
    } catch {
      // noop — already removed from UI
    }
  };

  return (
    <Box sx={{ p: 2, maxWidth: 960, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
        <Typography variant="h5" component="h1">
          My dashboards &amp; data
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setTemplatePickerOpen(true)}>
          New dashboard
        </Button>
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Build multiple layouts, open them in the editor, and add PostGIS layers on map widgets. Dashboards are
        saved to the server automatically.
      </Typography>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <DashboardIcon color="primary" fontSize="small" />
            <Typography variant="subtitle1" fontWeight={600}>
              Dashboards
            </Typography>
          </Stack>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : sortedDashboards.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No saved dashboards yet. Create one to start editing.
            </Typography>
          ) : (
            <List dense disablePadding>
              {sortedDashboards.map((d) => (
                <ListItem key={d.id} sx={{ py: 1, alignItems: 'flex-start' }}>
                  <ListItemText
                    primary={d.name}
                    secondary={
                      <>
                        <Typography component="span" variant="caption" display="block" color="text.secondary">
                          Updated {formatUpdated(d.updatedAt)}
                        </Typography>
                        <Typography component="span" variant="caption" color="text.disabled">
                          {d.id}
                        </Typography>
                      </>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title="View dashboard">
                        <IconButton
                          edge="end"
                          aria-label="View dashboard"
                          component={RouterLink}
                          to={`/dashboard-builder/${d.id}`}
                          size="small"
                        >
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit dashboard">
                        <IconButton
                          edge="end"
                          aria-label="Edit dashboard"
                          component={RouterLink}
                          to={`/dashboard-builder/${d.id}?edit=true`}
                          size="small"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Duplicate">
                        <IconButton
                          edge="end"
                          aria-label="Duplicate"
                          size="small"
                          onClick={() => handleDuplicate(d)}
                        >
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          edge="end"
                          aria-label="Delete"
                          size="small"
                          color="warning"
                          onClick={() => handleDelete(d.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      <DataSourcesPanel onLibraryChanged={loadDashboards} />

      {!loading && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
          <Button size="small" onClick={() => { setLoading(true); loadDashboards(); }}>
            Refresh
          </Button>
        </Typography>
      )}

      <DashboardTemplatePicker
        open={templatePickerOpen}
        onClose={() => setTemplatePickerOpen(false)}
        onSelect={handleTemplateSelected}
      />

      <Dialog open={createOpen} onClose={() => { setCreateOpen(false); chosenTemplate.current = null; }} maxWidth="xs" fullWidth>
        <DialogTitle>Name your dashboard</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Name"
              fullWidth
              size="small"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />
            {chosenTemplate.current && (
              <Typography variant="body2" color="text.secondary">
                Template: <strong>{chosenTemplate.current.name}</strong> &mdash; {chosenTemplate.current.description}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCreateOpen(false); chosenTemplate.current = null; }}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate}>
            Create &amp; open
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserContentPage;
